/**
 * Shopper notifications (docs/notifications.md): the categories a shopper sees and may opt out of,
 * the system types features fire, the template grammar tenants edit them in, and the renderer the
 * server, the dashboard preview and the tests share, so every reader of a template agrees.
 *
 * A template is text with `{placeholders}` and optional `[segments]`. A placeholder is a term in
 * the tenant's words (`{box}`, `{Boxes}`, `{forge}`), `{storeName}`, or one of the type's own
 * variables (`{amount}`, `{itemSummary}`). A segment in square brackets is dropped whole when any
 * placeholder inside it renders empty, so "is on its way.[ Tracking {trackingNumber}.]" reads
 * right with or without a tracking number. Brackets are reserved and do not nest.
 */
import { z } from "zod";

import { type Currency, formatAmount, formatAmountWithUnit, USD } from "./currency.js";
import {
  capitalizeTerm,
  STOREFRONT_TERM_KEYS,
  type StorefrontTerminology,
} from "./storefrontTerminology.js";

// ---- categories ------------------------------------------------------------------------------

/**
 * What a shopper opts in or out of. `orders`, `balance` and `account` are required: receipts and
 * account notices always arrive. The rest a shopper may turn off.
 */
export const NOTIFICATION_CATEGORIES = [
  "orders",
  "battles",
  "forge",
  "balance",
  "account",
  "marketing",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const REQUIRED_NOTIFICATION_CATEGORIES: readonly NotificationCategory[] = [
  "orders",
  "balance",
  "account",
];

/** Whether a shopper may turn the category off. */
export function isNotificationCategoryOptional(category: NotificationCategory): boolean {
  return !REQUIRED_NOTIFICATION_CATEGORIES.includes(category);
}

/** The category's name for shoppers and the dashboard; terms resolved in the tenant's words. */
export function notificationCategoryLabel(
  category: NotificationCategory,
  terms: StorefrontTerminology,
): string {
  switch (category) {
    case "orders":
      return "Orders and shipping";
    case "battles":
      return capitalizeTerm(terms.battle.plural);
    case "forge":
      return capitalizeTerm(terms.forge.singular);
    case "balance":
      return "Balance and payments";
    case "account":
      return "Account";
    case "marketing":
      return "News and offers";
  }
}

// ---- icons -----------------------------------------------------------------------------------

/**
 * The icons a notification may show, by lucide name: the storefront and the dashboard map each to
 * its lucide component, so a template stores a name, never markup.
 */
export const NOTIFICATION_ICONS = [
  "bell",
  "truck",
  "package",
  "package-x",
  "trophy",
  "swords",
  "hammer",
  "archive",
  "wallet",
  "credit-card",
  "gift",
  "badge-check",
  "shield-x",
  "id-card",
  "lock-open",
  "party-popper",
  "sparkles",
  "megaphone",
  "star",
  "tag",
  "flame",
  "zap",
  "heart",
  "coins",
  "box",
  "calendar",
  "clock",
  "percent",
  "rocket",
  "crown",
] as const;
export type NotificationIcon = (typeof NOTIFICATION_ICONS)[number];

// ---- placeholders ----------------------------------------------------------------------------

/** What a template renders against: the environment's words, currency and name. */
export interface NotificationRenderContext {
  terms: StorefrontTerminology;
  currency: Currency;
  storeName: string;
}

/**
 * The term placeholders every template may use, named after the default word so the token reads
 * the same whatever the tenant calls the thing: `{box}` / `{boxes}` / `{Box}` / `{Boxes}`.
 */
export const NOTIFICATION_TERM_TOKENS: Record<string, (terms: StorefrontTerminology) => string> =
  Object.fromEntries(
    STOREFRONT_TERM_KEYS.flatMap((key) => {
      const plural = pluralToken(key);
      return [
        [key, (terms: StorefrontTerminology) => terms[key].singular],
        [plural, (terms: StorefrontTerminology) => terms[key].plural],
        [
          capitalizeTerm(key),
          (terms: StorefrontTerminology) => capitalizeTerm(terms[key].singular),
        ],
        [
          capitalizeTerm(plural),
          (terms: StorefrontTerminology) => capitalizeTerm(terms[key].plural),
        ],
      ];
    }),
  );

function pluralToken(key: (typeof STOREFRONT_TERM_KEYS)[number]): string {
  switch (key) {
    case "box":
      return "boxes";
    case "inventory":
      return "inventories";
    default:
      return `${key}s`;
  }
}

/** A type's own placeholder, with what the dashboard shows beside it. */
export interface NotificationVariable {
  label: string;
}

/** Stored raw values, read leniently: a row whose data drifted still renders, minus the part. */
type RawData = Record<string, unknown>;

function text(data: RawData, key: string): string {
  const value = data[key];
  return typeof value === "string" ? value : "";
}

function cents(data: RawData, key: string): number | null {
  const value = data[key];
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : null;
}

function count(data: RawData, key: string): number {
  const value = data[key];
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : 0;
}

function names(data: RawData, key: string): string[] {
  const value = data[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function amount(data: RawData, key: string, ctx: NotificationRenderContext): string {
  const value = cents(data, key);
  return value === null ? "" : formatAmountWithUnit(value, ctx.currency);
}

/** "Charizard ex", "Charizard ex and Pikachu", "Charizard ex and 2 more items". */
function summarize(list: string[], ctx: NotificationRenderContext): string {
  const [first, second] = list;
  if (!first) return "";
  if (list.length === 1) return first;
  if (list.length === 2 && second) return `${first} and ${second}`;
  return `${first} and ${list.length - 1} more ${ctx.terms.item.plural}`;
}

/** "1 purchase", "12 purchases". */
function counted(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

// ---- system types ----------------------------------------------------------------------------

export interface NotificationAction {
  label: string;
  /** A storefront path ("/inventory") or an https URL; placeholders allowed. */
  url: string;
}

export interface NotificationTypeDefinition {
  category: Exclude<NotificationCategory, "marketing">;
  /** The dashboard's name for it. */
  label: string;
  /** When it fires, for the dashboard. */
  description: string;
  variables: Record<string, NotificationVariable>;
  /** The variables from the stored data, in the tenant's words and currency. */
  render: (data: RawData, ctx: NotificationRenderContext) => Record<string, string>;
  /** Representative stored data, for the dashboard preview and a test send. */
  sample: RawData;
  defaults: {
    title: string;
    body: string;
    icon: NotificationIcon;
    action: NotificationAction | null;
  };
}

const itemsBack = (names: string[], ctx: NotificationRenderContext): string => {
  if (names.length === 0) return "";
  const subject = summarize(names, ctx);
  return `${subject} ${names.length === 1 ? "is" : "are"} back in your ${ctx.terms.inventory.singular}.`;
};

/**
 * Every system notification, keyed by type. The order is the dashboard's, grouped by category.
 * Defaults name things only through placeholders, so a renamed term or a new currency reaches
 * every template without an edit.
 */
export const NOTIFICATION_TYPES = {
  order_shipped: {
    category: "orders",
    label: "Order shipped",
    description: "A withdrawal order is marked shipped.",
    variables: {
      itemSummary: { label: "What shipped" },
      trackingNumber: { label: "Tracking number (empty when none)" },
    },
    render: (data, ctx) => ({
      itemSummary: summarize(names(data, "itemNames"), ctx),
      trackingNumber: text(data, "trackingNumber"),
    }),
    sample: { itemNames: ["Charizard ex – 199/165"], trackingNumber: "1Z999AA10123456784" },
    defaults: {
      title: "Your order has shipped",
      body: "{itemSummary} is on its way.[ Tracking {trackingNumber}.]",
      icon: "truck",
      action: { label: "View {inventory}", url: "/inventory" },
    },
  },
  order_tracking_added: {
    category: "orders",
    label: "Tracking added",
    description: "A tracking number is added to an order that shipped without one.",
    variables: {
      itemSummary: { label: "What shipped" },
      trackingNumber: { label: "Tracking number" },
    },
    render: (data, ctx) => ({
      itemSummary: summarize(names(data, "itemNames"), ctx),
      trackingNumber: text(data, "trackingNumber"),
    }),
    sample: { itemNames: ["Charizard ex – 199/165"], trackingNumber: "1Z999AA10123456784" },
    defaults: {
      title: "Tracking for your order",
      body: "{itemSummary} can now be tracked: {trackingNumber}.",
      icon: "truck",
      action: null,
    },
  },
  order_cancelled: {
    category: "orders",
    label: "Order cancelled",
    description:
      "A whole withdrawal order is cancelled; says what came back and any shipping fee refund.",
    variables: {
      itemSummary: { label: "What was ordered" },
      returnedSummary: { label: "What is back in the inventory (empty when nothing)" },
      voucherValue: { label: "Value paid back as vouchers (empty when none)" },
      refundedFee: { label: "Shipping fee refunded (empty when none)" },
    },
    render: (data, ctx) => ({
      itemSummary: summarize(names(data, "itemNames"), ctx),
      returnedSummary: itemsBack(names(data, "returnedNames"), ctx),
      voucherValue: amount(data, "voucherValueCents", ctx),
      refundedFee: amount(data, "refundedFeeCents", ctx),
    }),
    sample: {
      itemNames: ["Charizard ex – 199/165", "Pikachu"],
      returnedNames: ["Pikachu"],
      voucherValueCents: 6240,
      refundedFeeCents: 500,
    },
    defaults: {
      title: "Your order was cancelled",
      body: "We couldn't ship {itemSummary}.[ {returnedSummary}][ {voucherValue} in {vouchers} was added to your {inventory}.][ Your {refundedFee} shipping fee was refunded.]",
      icon: "package-x",
      action: { label: "View {inventory}", url: "/inventory" },
    },
  },
  order_item_removed: {
    category: "orders",
    label: "Item removed from order",
    description: "One item of a larger order is cancelled; the rest still ships.",
    variables: {
      itemName: { label: "The removed item" },
      returnedSummary: { label: "Back in the inventory (empty when paid back)" },
      voucherValue: { label: "Value paid back as a voucher (empty when none)" },
    },
    render: (data, ctx) => ({
      itemName: text(data, "itemName"),
      returnedSummary: itemsBack(names(data, "returnedNames"), ctx),
      voucherValue: amount(data, "voucherValueCents", ctx),
    }),
    sample: { itemName: "Charizard ex – 199/165", returnedNames: [], voucherValueCents: 6240 },
    defaults: {
      title: "{Item} removed from your order",
      body: "We couldn't ship {itemName}, so it was taken off your order.[ {returnedSummary}][ {voucherValue} in {vouchers} was added to your {inventory}.] The rest is still on its way.",
      icon: "package",
      action: { label: "View {inventory}", url: "/inventory" },
    },
  },
  battle_won: {
    category: "battles",
    label: "Battle won",
    description: "A battle the shopper played in finishes and they won.",
    variables: {
      wonValue: { label: "What they won" },
      nextStep: { label: "Claim them, or already in the inventory" },
      battleId: { label: "The battle's id, for links" },
    },
    render: (data, ctx) => ({
      wonValue: amount(data, "wonValueCents", ctx),
      nextStep:
        data.claimed === true
          ? `They're in your ${ctx.terms.inventory.singular}.`
          : `Claim your ${ctx.terms.item.plural} to keep them.`,
      battleId: text(data, "battleId"),
    }),
    sample: { battleId: "sample", wonValueCents: 6240, claimed: false },
    defaults: {
      title: "You won the {battle}",
      body: "You won {wonValue} in {items}. {nextStep}",
      icon: "trophy",
      action: { label: "View {battle}", url: "/battles/{battleId}" },
    },
  },
  battle_lost: {
    category: "battles",
    label: "Battle lost",
    description: "A battle the shopper played in finishes and they did not win.",
    variables: {
      prizeName: { label: "The consolation prize (empty when none)" },
      nextStep: { label: "Claim it, or already in the inventory" },
      battleId: { label: "The battle's id, for links" },
    },
    render: (data, ctx) => {
      const prize = text(data, "prizeName");
      return {
        prizeName: prize,
        nextStep: !prize
          ? ""
          : data.claimed === true
            ? `It's in your ${ctx.terms.inventory.singular}.`
            : "Claim it to keep it.",
        battleId: text(data, "battleId"),
      };
    },
    sample: { battleId: "sample", prizeName: "Mystery Bulk Card", claimed: false },
    defaults: {
      title: "The {battle} is over",
      body: "You didn't win this time.[ You still get {prizeName}. {nextStep}]",
      icon: "swords",
      action: { label: "View {battle}", url: "/battles/{battleId}" },
    },
  },
  forge_earnings_summary: {
    category: "forge",
    label: "Daily earnings",
    description: "Once a day, when a creator's boxes earned commission since the last summary.",
    variables: {
      earned: { label: "Earned in the last day" },
      purchases: { label: "How many purchases earned it" },
      unclaimed: { label: "Waiting to be claimed" },
    },
    render: (data, ctx) => ({
      earned: amount(data, "earnedCents", ctx),
      purchases: counted(count(data, "purchaseCount"), "purchase", "purchases"),
      unclaimed: amount(data, "unclaimedCents", ctx),
    }),
    sample: { earnedCents: 420, purchaseCount: 12, unclaimedCents: 980 },
    defaults: {
      title: "Your {boxes} earned {earned}",
      body: "{purchases} in the last day.[ You have {unclaimed} to claim.]",
      icon: "hammer",
      action: { label: "Claim in {Forge}", url: "/forge/mine" },
    },
  },
  forge_box_archived: {
    category: "forge",
    label: "Box archived",
    description: "A creator's box nobody opened in 30 days is archived.",
    variables: { boxName: { label: "The archived box" } },
    render: (data) => ({ boxName: text(data, "boxName") }),
    sample: { boxName: "Neon Vault" },
    defaults: {
      title: "{Box} archived",
      body: "{boxName} wasn't opened in 30 days, so it was archived. You can bring it back from {Forge}.",
      icon: "archive",
      action: { label: "Open {Forge}", url: "/forge/mine" },
    },
  },
  balance_purchase_completed: {
    category: "balance",
    label: "Purchase complete",
    description: "A top-up pack payment settles and the balance is credited.",
    variables: {
      amount: { label: "Added to the balance" },
      packName: { label: "The pack bought" },
    },
    render: (data, ctx) => ({
      amount: amount(data, "creditedCents", ctx),
      packName: text(data, "packName"),
    }),
    sample: { creditedCents: 5000, packName: "Starter pack" },
    defaults: {
      title: "Purchase complete",
      body: "{amount} was added to your balance.",
      icon: "wallet",
      action: null,
    },
  },
  balance_purchase_failed: {
    category: "balance",
    label: "Payment failed",
    description: "A delayed top-up payment fails after the shopper left checkout.",
    variables: {
      packName: { label: "The pack" },
      price: { label: "The price, in dollars" },
    },
    render: (data) => {
      const price = cents(data, "priceCents");
      return {
        packName: text(data, "packName"),
        price: price === null ? "" : formatAmount(price, USD),
      };
    },
    sample: { packName: "Starter pack", priceCents: 4999 },
    defaults: {
      title: "Payment didn't go through",
      body: "Your payment[ of {price}] for {packName} failed, so nothing was added to your balance.",
      icon: "credit-card",
      action: null,
    },
  },
  balance_credited: {
    category: "balance",
    label: "Balance added",
    description: "Staff add to the shopper's balance from the dashboard.",
    variables: { amount: { label: "The amount added" } },
    render: (data, ctx) => ({ amount: amount(data, "amountCents", ctx) }),
    sample: { amountCents: 1000 },
    defaults: {
      title: "Balance added",
      body: "{storeName} added {amount} to your balance.",
      icon: "gift",
      action: null,
    },
  },
  kyc_verified: {
    category: "account",
    label: "Identity verified",
    description: "Identity verification is approved.",
    variables: {},
    render: () => ({}),
    sample: {},
    defaults: {
      title: "Identity verified",
      body: "You can now have {items} shipped to you.",
      icon: "badge-check",
      action: null,
    },
  },
  kyc_declined: {
    category: "account",
    label: "Identity not verified",
    description: "Identity verification is declined. Never says why.",
    variables: {},
    render: () => ({}),
    sample: {},
    defaults: {
      title: "We couldn't verify your identity",
      body: "Contact support if you need help.",
      icon: "shield-x",
      action: null,
    },
  },
  kyc_resubmission: {
    category: "account",
    label: "Verify again",
    description: "Identity verification asks the shopper to try again.",
    variables: {},
    render: () => ({}),
    sample: {},
    defaults: {
      title: "Verify your identity again",
      body: "Your identity check needs another try.",
      icon: "id-card",
      action: { label: "Verify", url: "/account" },
    },
  },
  account_unlocked: {
    category: "account",
    label: "Account unlocked",
    description: "Staff lift a lock on the account.",
    variables: {},
    render: () => ({}),
    sample: {},
    defaults: {
      title: "Your account is unlocked",
      body: "You can open {boxes} and claim {items} again.",
      icon: "lock-open",
      action: null,
    },
  },
  welcome: {
    category: "account",
    label: "Welcome",
    description: "Once, when a shopper signs up on the storefront.",
    variables: {},
    render: () => ({}),
    sample: {},
    defaults: {
      title: "Welcome to {storeName}",
      body: "Open your first {box} to start your collection.",
      icon: "party-popper",
      action: { label: "Browse {boxes}", url: "/boxes" },
    },
  },
} satisfies Record<string, NotificationTypeDefinition>;

export type NotificationType = keyof typeof NOTIFICATION_TYPES;
export const NOTIFICATION_TYPE_KEYS = Object.keys(NOTIFICATION_TYPES) as NotificationType[];

/** The definition of a system type, typed as the general shape. */
export function notificationTypeDefinition(type: NotificationType): NotificationTypeDefinition {
  return NOTIFICATION_TYPES[type];
}

export function isNotificationType(value: string): value is NotificationType {
  return Object.hasOwn(NOTIFICATION_TYPES, value);
}

// ---- template grammar ------------------------------------------------------------------------

const PLACEHOLDER = /\{([A-Za-z][A-Za-z0-9]*)\}/g;

/**
 * Why template text is refused, or null when it is fine: an unknown placeholder, a bracket left
 * open or closed alone, or brackets nested. `allowed` is the type's own variables (none for a
 * marketing notification, which has terms and `{storeName}` only).
 */
export function notificationTemplateIssue(
  value: string,
  allowed: readonly string[] = [],
): string | null {
  let depth = 0;
  for (const char of value) {
    if (char === "[") {
      depth += 1;
      if (depth > 1) return "Optional segments in [brackets] can't be nested.";
    } else if (char === "]") {
      depth -= 1;
      if (depth < 0) return "A ] has no [ to close.";
    }
  }
  if (depth !== 0) return "A [ is never closed.";
  for (const match of value.matchAll(PLACEHOLDER)) {
    const name = match[1] ?? "";
    if (name === "storeName" || Object.hasOwn(NOTIFICATION_TERM_TOKENS, name)) continue;
    if (allowed.includes(name)) continue;
    return `{${name}} isn't a placeholder this notification has.`;
  }
  return null;
}

/** Fills one placeholder; "" for an unknown name. */
function placeholderValue(
  name: string,
  values: Record<string, string>,
  ctx: NotificationRenderContext,
): string {
  if (name === "storeName") return ctx.storeName;
  const term = NOTIFICATION_TERM_TOKENS[name];
  if (term) return term(ctx.terms);
  return values[name] ?? "";
}

function fill(
  segment: string,
  values: Record<string, string>,
  ctx: NotificationRenderContext,
): { text: string; empty: boolean } {
  let empty = false;
  const filled = segment.replace(PLACEHOLDER, (_, name: string) => {
    const value = placeholderValue(name, values, ctx);
    if (!value) empty = true;
    return value;
  });
  return { text: filled, empty };
}

/**
 * Template text rendered: placeholders filled, a bracketed segment dropped when any placeholder
 * in it is empty, then whitespace tidied. Text with a malformed bracket renders with the
 * brackets stripped rather than failing, since stored text was checked when it was saved.
 */
export function renderNotificationText(
  template: string,
  values: Record<string, string>,
  ctx: NotificationRenderContext,
): string {
  const out: string[] = [];
  for (const part of template.split(/(\[[^[\]]*\])/)) {
    if (part.startsWith("[") && part.endsWith("]")) {
      const segment = fill(part.slice(1, -1), values, ctx);
      if (!segment.empty) out.push(segment.text);
    } else {
      out.push(fill(part.replace(/[[\]]/g, ""), values, ctx).text);
    }
  }
  return out
    .join("")
    .replace(/\s+/g, " ")
    .replace(/\s+([.,!?])/g, "$1")
    .trim();
}

/** An action rendered; null when its label or URL renders empty or the URL is not allowed. */
export function renderNotificationAction(
  action: NotificationAction | null,
  values: Record<string, string>,
  ctx: NotificationRenderContext,
): NotificationAction | null {
  if (!action) return null;
  const label = renderNotificationText(action.label, values, ctx);
  const encoded = Object.fromEntries(
    Object.entries(values).map(([k, v]) => [k, encodeURIComponent(v)]),
  );
  // A link whose placeholder is empty points nowhere ("/battles/"), so the action goes.
  const url = fill(action.url, encoded, ctx);
  const href = url.text.trim();
  if (!label || url.empty || !isNotificationActionUrl(href)) return null;
  return { label, url: href };
}

/** A storefront path ("/boxes", not "//host") or an https URL. */
export function isNotificationActionUrl(url: string): boolean {
  if (url.startsWith("/")) return !url.startsWith("//") && !url.includes("\\");
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
}

// ---- stored shapes ---------------------------------------------------------------------------

export const NOTIFICATION_TITLE_MAX = 80;
export const NOTIFICATION_BODY_MAX = 300;
export const NOTIFICATION_ACTION_LABEL_MAX = 40;

export const notificationActionSchema = z.object({
  label: z.string().trim().min(1).max(NOTIFICATION_ACTION_LABEL_MAX),
  url: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .refine(
      (url) => url.startsWith("/") || url.startsWith("https://"),
      "Use a storefront path like /boxes or an https:// link.",
    ),
});

/**
 * A tenant's edit of one system type, per environment: only what they changed; an absent key
 * follows the default. `action: null` and `imageUrl: null` mean "none", unlike absent.
 */
export const notificationTemplateOverrideSchema = z.object({
  enabled: z.boolean().optional(),
  title: z.string().trim().min(1).max(NOTIFICATION_TITLE_MAX).optional(),
  body: z.string().trim().min(1).max(NOTIFICATION_BODY_MAX).optional(),
  icon: z.enum(NOTIFICATION_ICONS).optional(),
  action: notificationActionSchema.nullable().optional(),
  imageUrl: z.string().url().max(2000).nullable().optional(),
});
export type NotificationTemplateOverride = z.infer<typeof notificationTemplateOverrideSchema>;

/** `storefront_configs.notification_templates`: an override per edited type, `{}` = every default. */
export const notificationTemplateOverridesSchema = z.partialRecord(
  z.enum(NOTIFICATION_TYPE_KEYS as [NotificationType, ...NotificationType[]]),
  notificationTemplateOverrideSchema,
);
export type NotificationTemplateOverrides = z.infer<typeof notificationTemplateOverridesSchema>;

/** A system type's template as it stands: the default with the tenant's edit on top. */
export interface ResolvedNotificationTemplate {
  enabled: boolean;
  title: string;
  body: string;
  icon: NotificationIcon;
  action: NotificationAction | null;
  imageUrl: string | null;
}

export function resolveNotificationTemplate(
  type: NotificationType,
  override: NotificationTemplateOverride | undefined,
): ResolvedNotificationTemplate {
  const { defaults } = notificationTypeDefinition(type);
  return {
    enabled: override?.enabled ?? true,
    title: override?.title ?? defaults.title,
    body: override?.body ?? defaults.body,
    icon: override?.icon ?? defaults.icon,
    action: override?.action === undefined ? defaults.action : override.action,
    imageUrl: override?.imageUrl ?? null,
  };
}

/** Why an override is refused, naming the field: a placeholder the type does not have. */
export function notificationTemplateOverrideIssue(
  type: NotificationType,
  override: NotificationTemplateOverride,
): string | null {
  const allowed = Object.keys(notificationTypeDefinition(type).variables);
  for (const [field, value] of [
    ["title", override.title],
    ["body", override.body],
    ["action label", override.action?.label],
    ["action link", override.action?.url],
  ] as const) {
    if (value === undefined) continue;
    const issue = notificationTemplateIssue(value, allowed);
    if (issue) return `${field}: ${issue}`;
  }
  return null;
}

/** A marketing notification's content, as composed. Terms and `{storeName}` only. */
export const notificationContentSchema = z.object({
  title: z.string().trim().min(1).max(NOTIFICATION_TITLE_MAX),
  body: z.string().trim().min(1).max(NOTIFICATION_BODY_MAX),
  icon: z.enum(NOTIFICATION_ICONS),
  action: notificationActionSchema.nullable(),
  imageUrl: z.string().url().max(2000).nullable(),
});
export type NotificationContent = z.infer<typeof notificationContentSchema>;

/** Why composed content is refused, naming the field, or null. */
export function notificationContentIssue(content: NotificationContent): string | null {
  for (const [field, value] of [
    ["title", content.title],
    ["body", content.body],
    ["action label", content.action?.label],
    ["action link", content.action?.url],
  ] as const) {
    if (value === undefined) continue;
    const issue = notificationTemplateIssue(value);
    if (issue) return `${field}: ${issue}`;
  }
  return null;
}

/** A system notification, or a marketing one, rendered for a reader. */
export function renderNotification(
  input:
    | {
        kind: "system";
        type: NotificationType;
        data: RawData;
        template: ResolvedNotificationTemplate;
      }
    | { kind: "marketing"; content: NotificationContent },
  ctx: NotificationRenderContext,
): {
  title: string;
  body: string;
  icon: NotificationIcon;
  action: NotificationAction | null;
  imageUrl: string | null;
} {
  if (input.kind === "marketing") {
    const { content } = input;
    return {
      title: renderNotificationText(content.title, {}, ctx),
      body: renderNotificationText(content.body, {}, ctx),
      icon: content.icon,
      action: renderNotificationAction(content.action, {}, ctx),
      imageUrl: content.imageUrl,
    };
  }
  const values = notificationTypeDefinition(input.type).render(input.data, ctx);
  return {
    title: renderNotificationText(input.template.title, values, ctx),
    body: renderNotificationText(input.template.body, values, ctx),
    icon: input.template.icon,
    action: renderNotificationAction(input.template.action, values, ctx),
    imageUrl: input.template.imageUrl,
  };
}

// ---- wire shapes -----------------------------------------------------------------------------

export const notificationSchema = z.object({
  id: z.string(),
  category: z.enum(NOTIFICATION_CATEGORIES),
  /** The system type, or `marketing`. */
  type: z.string(),
  title: z.string(),
  body: z.string(),
  icon: z.enum(NOTIFICATION_ICONS),
  action: z.object({ label: z.string(), url: z.string() }).nullable(),
  imageUrl: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

/** GET /v1/me/notifications: newest first. */
export const notificationListResponseSchema = z.object({
  data: z.array(notificationSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.int(),
});
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

export const notificationListQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  unread: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => value === "true"),
});

/** POST /v1/me/notifications/read: the ids named, or every unread one. */
export const markNotificationsReadRequestSchema = z.union([
  z.object({ ids: z.array(z.string().min(1)).min(1).max(100) }),
  z.object({ all: z.literal(true) }),
]);
export type MarkNotificationsReadRequest = z.infer<typeof markNotificationsReadRequestSchema>;

/** One category as the shopper's settings show it: those the environment can send only. */
export const notificationCategorySettingSchema = z.object({
  category: z.enum(NOTIFICATION_CATEGORIES),
  label: z.string(),
  required: z.boolean(),
  enabled: z.boolean(),
});

/** GET/PUT /v1/me/notification-settings. */
export const notificationSettingsResponseSchema = z.object({
  categories: z.array(notificationCategorySettingSchema),
});
export type NotificationSettingsResponse = z.infer<typeof notificationSettingsResponseSchema>;

export const updateNotificationSettingsRequestSchema = z.object({
  /** The optional categories the shopper turns off; a required one named here is refused. */
  optedOut: z.array(z.enum(NOTIFICATION_CATEGORIES)).max(NOTIFICATION_CATEGORIES.length),
});

/** What the realtime channel carries: a notification as it was written, rendered. */
export const notificationEventSchema = z.object({
  type: z.literal("NOTIFICATION_CREATED"),
  notification: notificationSchema,
});
export type NotificationEvent = z.infer<typeof notificationEventSchema>;

/** One shopper's channel on the stage's Event API; only their own realtime token subscribes. */
export function notificationChannel(environmentId: string, endUserId: string): string {
  return `/notifications/${environmentId}/${endUserId}`;
}

/** How long a notification stays readable (boxed parity: 60 days). */
export const NOTIFICATION_RETENTION_DAYS = 60;
